import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Handshake, Plus, ArrowsClockwise, CaretRight, Medal, FolderOpen } from "@phosphor-icons/react";
import { toast } from "sonner";
import API from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { ResellerLeaderboard } from "../../components/platform/ResellerLeaderboard";
import { PayoutReminderCard } from "../../components/platform/PayoutReminderCard";
import { ResellerTiersDialog } from "../../components/platform/ResellerTiersDialog";
import { TierBadge } from "../../components/reseller/TierBadge";
import { ResellerLoginChoice, ResellerLoginResult, loginChoiceProblem } from "../../components/platform/ResellerLoginChoice";
import { money } from "../../lib/resellerFormat";

export { money };
const EMPTY = { name: "", company: "", email: "", phone: "", handle: "", tier: "", commission_pct: "", max_discount_pct: 10, first_code_pct: 10, payout_details: "", notes: "", headline: "", intro: "" };

function Stat({ label, value, testId }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid={testId}>
      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">{label}</p>
      <p className="text-2xl font-bold font-[Chivo] mt-1">{value}</p>
    </div>
  );
}

export function ResellerForm({ form, setForm, create = false, tiers = [] }) {
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const handle = (form.handle || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
  const tierPick = (key) => {
    const t = tiers.find((x) => x.key === key);
    setForm({ ...form, tier: key, ...(t ? { commission_pct: t.commission_pct } : {}) });
  };
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <div><Label className="text-xs">Reseller name *</Label><Input value={form.name} onChange={set("name")} className="mt-1" data-testid="reseller-name-input" /></div>
      <div><Label className="text-xs">Company</Label><Input value={form.company} onChange={set("company")} className="mt-1" data-testid="reseller-company-input" /></div>
      <div><Label className="text-xs">E-mail (portal login)</Label><Input type="email" value={form.email} onChange={set("email")} className="mt-1" data-testid="reseller-email-input" /></div>
      <div><Label className="text-xs">Phone</Label><Input value={form.phone} onChange={set("phone")} className="mt-1" data-testid="reseller-phone-input" /></div>
      {create && (
        <div>
          <Label className="text-xs">Code prefix *</Label>
          <Input value={handle} onChange={(e) => setForm({ ...form, handle: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "") })} placeholder="PETE" className="mt-1 uppercase tracking-wider" maxLength={16} data-testid="reseller-handle-input" />
          <p className="text-[10px] text-muted-foreground mt-1">Letters/digits, not ending in a digit. Codes become <b>{handle || "PETE"}10</b>, <b>{handle || "PETE"}5</b>…</p>
        </div>
      )}
      {tiers.length > 0 && (
        <div>
          <Label className="text-xs">Tier</Label>
          <select value={form.tier || ""} onChange={(e) => tierPick(e.target.value)} className="mt-1 flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm" data-testid="reseller-tier-select">
            {tiers.map((t) => <option key={t.key} value={t.key}>{t.name} · {t.commission_pct}%</option>)}
          </select>
          <p className="text-[10px] text-muted-foreground mt-1">Picking a tier sets the commission rate below.</p>
        </div>
      )}
      <div><Label className="text-xs">Commission % of every payment</Label><Input type="number" min="0" max="100" step="0.5" value={form.commission_pct} onChange={set("commission_pct")} className="mt-1" data-testid="reseller-commission-input" /></div>
      <div><Label className="text-xs">Max discount they may offer %</Label><Input type="number" min="0" max="50" step="1" value={form.max_discount_pct} onChange={set("max_discount_pct")} className="mt-1" data-testid="reseller-maxdiscount-input" /></div>
      {create && <div><Label className="text-xs">First code discount % (blank = none yet)</Label><Input type="number" min="0" max="50" step="1" value={form.first_code_pct} onChange={set("first_code_pct")} className="mt-1" data-testid="reseller-firstcode-input" /></div>}
      <div className="sm:col-span-2"><Label className="text-xs">Payout details (bank, reference)</Label><Input value={form.payout_details} onChange={set("payout_details")} className="mt-1" data-testid="reseller-payout-input" /></div>
      <div className="sm:col-span-2"><Label className="text-xs">Notes (internal)</Label><Textarea value={form.notes} onChange={set("notes")} rows={2} className="mt-1" data-testid="reseller-notes-input" /></div>
      <div className="sm:col-span-2 rounded-lg border border-dashed border-border p-3 space-y-2">
        <p className="text-xs font-semibold">Public partner page <span className="font-normal text-muted-foreground">— fixlr.app/partner/{handle || form.handle || "PETE"}10 · the reseller can edit this in their portal</span></p>
        <div><Label className="text-xs">Headline</Label><Input value={form.headline || ""} onChange={set("headline")} maxLength={120} placeholder={`${form.name || "Your partner"} can get you Fixlr for less`} className="mt-1" data-testid="reseller-headline-input" /><p className="text-[10px] text-muted-foreground mt-0.5">{(form.headline || "").length}/120</p></div>
        <div><Label className="text-xs">Short intro</Label><Textarea value={form.intro || ""} onChange={set("intro")} rows={2} maxLength={600} placeholder="Two or three sentences about who they are and why Fixlr — leave blank for the standard text" className="mt-1" data-testid="reseller-intro-input" /><p className="text-[10px] text-muted-foreground mt-0.5">{(form.intro || "").length}/600</p></div>
      </div>
    </div>
  );
}

export default function ResellersPage() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [tiersOpen, setTiersOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [login, setLogin] = useState({ mode: "invite", password: "", forceChange: true });
  const [created, setCreated] = useState(null);
  const tiers = data?.totals?.tiers || [];

  const load = useCallback(async () => {
    try { const { data } = await API.get("/platform/resellers"); setData(data); } catch { toast.error("Couldn't load resellers"); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const openCreate = () => {
    const first = tiers[0];
    setForm({ ...EMPTY, tier: first?.key || "", commission_pct: first ? first.commission_pct : 10 });
    setLogin({ mode: "invite", password: "", forceChange: true });
    setCreated(null);
    setOpen(true);
  };

  const hasEmail = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test((form.email || "").trim());
  const create = async () => {
    const problem = loginChoiceProblem(login, hasEmail);
    if (problem) return toast.error(problem);
    setBusy(true);
    try {
      const { data: r } = await API.post("/platform/resellers", form);
      let loginRes = null;
      if (login.mode !== "later") {
        try {
          const { data: l } = await API.post(`/platform/resellers/${r.id}/invite`, { mode: login.mode, password: login.mode === "password" ? login.password : undefined, force_change: login.forceChange });
          loginRes = l;
        } catch (e) { toast.error(e.response?.data?.detail || "Reseller created, but the portal login couldn't be set up — use the Portal login tab"); }
      }
      toast.success(`Reseller ${r.name} created`);
      setCreated({ ...r, login: loginRes });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't create the reseller"); }
    setBusy(false);
  };
  const closeCreate = () => { setOpen(false); setCreated(null); setForm(EMPTY); };
  const recalc = async () => {
    setBusy(true);
    try { const { data } = await API.post("/platform/resellers/recalculate"); toast.success(`${data.created} new commission entr${data.created === 1 ? "y" : "ies"} found`); load(); }
    catch { toast.error("Recalculation failed"); }
    setBusy(false);
  };

  const t = data?.totals || {};
  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="resellers-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold font-[Chivo] flex items-center gap-2"><Handshake size={26} weight="duotone" className="text-primary" /> Resellers</h1>
          <p className="text-sm text-muted-foreground mt-1">Partners who sell Fixlr. Their code (e.g. <b>PETE10</b>) gives the new tenant the discount and earns the reseller a share of every payment for as long as the tenant stays live.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/platform/resellers/resources" className="inline-flex items-center h-8 rounded-md border border-input bg-background px-3 text-xs font-medium hover:bg-muted" data-testid="resellers-resources-btn"><FolderOpen size={14} className="mr-1" /> Resources hub</Link>
          <Button variant="outline" size="sm" onClick={() => setTiersOpen(true)} data-testid="resellers-tiers-btn"><Medal size={14} className="mr-1" /> Tiers</Button>
          <Button variant="outline" size="sm" onClick={recalc} disabled={busy} data-testid="resellers-recalc-btn"><ArrowsClockwise size={14} className="mr-1" /> Recalculate</Button>
          <Button size="sm" onClick={openCreate} data-testid="resellers-add-btn"><Plus size={14} className="mr-1" /> New reseller</Button>
        </div>
      </div>

      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <Stat label="Active resellers" value={`${t.active ?? 0} / ${t.resellers ?? 0}`} testId="resellers-stat-active" />
        <Stat label="Tenants via resellers" value={t.tenants ?? 0} testId="resellers-stat-tenants" />
        <Stat label="Commission this month" value={money(t.this_month_commission)} testId="resellers-stat-month" />
        <Stat label="Unpaid commission" value={money(t.unpaid_commission)} testId="resellers-stat-unpaid" />
      </div>

      <PayoutReminderCard />

      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-[10px] uppercase tracking-wider text-muted-foreground">
            <tr><th className="text-left px-4 py-2">Reseller</th><th className="text-left px-4 py-2">Tier</th><th className="text-left px-4 py-2">Codes</th><th className="text-right px-4 py-2">Tenants</th><th className="text-right px-4 py-2">Rate</th><th className="text-right px-4 py-2">This month</th><th className="text-right px-4 py-2">Unpaid</th><th className="px-4 py-2">Login</th><th /></tr>
          </thead>
          <tbody>
            {!data && <tr><td colSpan={9} className="px-4 py-8 text-center text-muted-foreground">Loading…</td></tr>}
            {data && data.resellers.length === 0 && <tr><td colSpan={9} className="px-4 py-10 text-center text-muted-foreground" data-testid="resellers-empty">No resellers yet — add your first partner.</td></tr>}
            {data?.resellers.map((r) => (
              <tr key={r.id} className="border-t border-border hover:bg-muted/30" data-testid={`reseller-row-${r.id}`}>
                <td className="px-4 py-3">
                  <Link to={`/platform/resellers/${r.id}`} className="font-semibold hover:underline" data-testid={`reseller-link-${r.id}`}>{r.name}</Link>
                  <p className="text-[11px] text-muted-foreground">{r.company || r.email || "—"} {r.status !== "active" && <Badge variant="secondary" className="ml-1 text-[9px]">Paused</Badge>}{r.pipeline?.open ? <span className="ml-1" data-testid={`reseller-pipeline-${r.id}`}>· {r.pipeline.open} open prospect{r.pipeline.open === 1 ? "" : "s"}{r.pipeline.overdue ? <span className="text-red-600"> ({r.pipeline.overdue} overdue)</span> : null}</span> : null}</p>
                </td>
                <td className="px-4 py-3">
                  <TierBadge tier={r.tier_info?.current} testId={`reseller-tier-${r.id}`} />
                  {r.tier_info?.upgrade_suggested && <p className="text-[10px] text-amber-700 dark:text-amber-400 mt-0.5" data-testid={`reseller-tier-suggest-${r.id}`}>Suggest {r.tier_info.suggested.name}</p>}
                </td>
                <td className="px-4 py-3 text-xs">{r.codes.length ? r.codes.map((c) => <span key={c.id} className={`inline-block mr-1 mb-1 rounded px-1.5 py-0.5 font-mono text-[11px] ${c.active ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground line-through"}`}>{c.code}</span>) : <span className="text-muted-foreground">none</span>}</td>
                <td className="px-4 py-3 text-right">{r.summary.live_tenants}<span className="text-muted-foreground">/{r.summary.tenants}</span></td>
                <td className="px-4 py-3 text-right">{r.commission_pct}%</td>
                <td className="px-4 py-3 text-right">{money(r.summary.this_month_commission)}</td>
                <td className="px-4 py-3 text-right font-semibold" data-testid={`reseller-unpaid-${r.id}`}>{money(r.summary.unpaid_commission)}</td>
                <td className="px-4 py-3 text-xs">{r.login ? (r.login.invite_status === "pending" ? <Badge className="bg-amber-100 text-amber-800 text-[10px]">Invite pending</Badge> : <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Active</Badge>) : <span className="text-muted-foreground">No login</span>}</td>
                <td className="px-2 py-3 text-right"><Link to={`/platform/resellers/${r.id}`} className="text-muted-foreground hover:text-foreground"><CaretRight size={16} /></Link></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ResellerLeaderboard tiers={tiers} />

      <Dialog open={open} onOpenChange={(o) => { if (!o) closeCreate(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="reseller-create-dialog">
          <DialogHeader><DialogTitle>{created ? "Reseller created" : "New reseller"}</DialogTitle><DialogDescription>{created ? "Here's what to pass on." : "They earn the commission % on every payment their tenants make. The discount cap limits the codes they can hand out."}</DialogDescription></DialogHeader>
          {created ? (
            <ResellerLoginResult result={created} password={login.password} onDone={closeCreate} />
          ) : (
            <>
              <ResellerForm form={form} setForm={setForm} create tiers={tiers} />
              <ResellerLoginChoice value={login} onChange={setLogin} hasEmail={hasEmail} />
              <DialogFooter>
                <Button variant="outline" onClick={closeCreate}>Cancel</Button>
                <Button onClick={create} disabled={busy || !form.name || !form.handle} data-testid="reseller-create-submit">{busy ? "Saving…" : login.mode === "invite" && hasEmail ? "Create & send invite" : "Create reseller"}</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <ResellerTiersDialog open={tiersOpen} onClose={() => setTiersOpen(false)} onSaved={load} />
    </div>
  );
}
